import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import TorusAnimation from './TorusAnimation';
import RotateOverlay from './RotateOverlay';
import './LandingPage.css';
import logo from '../assets/logo.svg';

import imgVis from '../assets/solution_visualization.png';
import imgDrills from '../assets/solution_drills.png';
import imgTrack from '../assets/solution_tracking.png';

const CONTENT = {
  fr: {
    nav: {
      cta: "Voir l'invisible",
      lang: "EN"
    },
    hero: {
      h1: "Vois ta voix.",
      h2: "Le tout premier miroir vocal.",
      cta: "Voir l'invisible",
      appStore: "Télécharger sur l'App Store"
    },
    beta: {
      title: "Accès sur invitation",
      desc: "VOYX est en beta privée. Laisse ton email pour recevoir une invitation TestFlight.",
      placeholder: "ton@email.com",
      cta: "Demander l'accès",
      submitting: "Envoi...",
      success: "Tu es sur la liste. On te contacte très vite.",
      already: "Tu es déjà inscrit, patience, ça arrive.",
      error: "Erreur, réessaie.",
      invalid: "Entre un email valide."
    },
    section_presence: "Une présence pour révéler la tienne\u00A0: Voxy",
    section_reveal: {
      lines: ["Miroir vocal", "Interface", "Intelligence"],
      punchline: "Tu vois le truc ?",
      cards: [
        { title: "Miroir vocal", desc: "Le Torus traduit ta voix en mouvement et en couleur. En temps réel." },
        { title: "Interface", desc: "Touche, swipe, parle. Voxy réagit au toucher." },
        { title: "Intelligence", desc: "Plus tu parles, plus Voxy te connaît." }
      ]
    },
    section_modes_title: "Le système d'exploitation de ta voix\u00A0: VOYX OS",
    section_cta: {
      opensource: "VoYXMirror est open source.",
      github: "GitHub"
    },
    section3: {
      features: [
        {
          title: "Profil",
          desc: "Revois ton animation, tes métriques, ta voix. Seconde par seconde.",
          img: imgDrills
        },
        {
          title: "Miroir",
          desc: "Ta voix, en temps réel. Le Torus traduit chaque variation : volume, débit, fréquence, mélodie. En mouvement et en couleur. Pendant que tu parles.",
          img: imgVis
        },
        {
          title: "Capture",
          desc: "Filme-toi avec le Torus. Partage ce que ta voix a créé.",
          img: imgTrack
        }
      ]
    },
    footer: {
      links: [
        { label: "À propos", path: "/about" },
        { label: "Confidentialité", path: "/privacy" },
        { label: "Contact", path: "/contact" }
      ]
    }
  },
  en: {
    nav: {
      cta: "See the invisible",
      lang: "FR"
    },
    hero: {
      h1: "See your voice.",
      h2: "The very first vocal mirror.",
      cta: "See the invisible",
      appStore: "Download on the App Store"
    },
    beta: {
      title: "Access by invitation",
      desc: "VOYX is in private beta. Leave your email to receive a TestFlight invitation.",
      placeholder: "your@email.com",
      cta: "Request access",
      submitting: "Sending...",
      success: "You're on the list. We'll reach out soon.",
      already: "You're already signed up. Hang tight.",
      error: "Error, try again.",
      invalid: "Enter a valid email."
    },
    section_presence: "A presence to reveal yours: Voxy",
    section_reveal: {
      lines: ["Vocal mirror", "Interface", "Intelligence"],
      punchline: "Are you getting it?",
      cards: [
        { title: "Vocal mirror", desc: "The Torus translates your voice into movement and color. In real time." },
        { title: "Interface", desc: "Touch, swipe, speak. Voxy responds to touch." },
        { title: "Intelligence", desc: "The more you speak, the more Voxy knows you." }
      ]
    },
    section_modes_title: "The operating system of your voice: VOYX OS",
    section_cta: {
      opensource: "VoYXMirror is open source.",
      github: "GitHub"
    },
    section3: {
      features: [
        {
          title: "Profile",
          desc: "Replay your animation, your metrics, your voice. Second by second.",
          img: imgDrills
        },
        {
          title: "Mirror",
          desc: "Your voice, in real time. The Torus translates every variation: volume, pace, frequency, melody. Into movement and color. While you speak.",
          img: imgVis
        },
        {
          title: "Capture",
          desc: "Film yourself with the Torus. Share what your voice created.",
          img: imgTrack
        }
      ]
    },
    footer: {
      links: [
        { label: "About", path: "/about" },
        { label: "Privacy", path: "/privacy" },
        { label: "Contact", path: "/contact" }
      ]
    }
  }
};

const GitHubIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 496 512" fill="currentColor">
    <path d="M165.9 397.4c0 2-2.3 3.6-5.2 3.6-3.3.3-5.6-1.3-5.6-3.6 0-2 2.3-3.6 5.2-3.6 3-.3 5.6 1.3 5.6 3.6zm-31.1-4.5c-.7 2 1.3 4.3 4.3 4.9 2.6 1 5.6 0 6.2-2s-1.3-4.3-4.3-5.2c-2.6-.7-5.5.3-6.2 2.3zm44.2-1.7c-2.9.7-4.9 2.6-4.6 4.9.3 2 2.9 3.3 5.9 2.6 2.9-.7 4.9-2.6 4.6-4.6-.3-1.9-3-3.2-5.9-2.9zM244.8 8C106.1 8 0 113.3 0 252c0 110.9 69.8 205.8 169.5 239.2 12.8 2.3 17.3-5.6 17.3-12.1 0-6.2-.3-40.4-.3-61.4 0 0-70 15-84.7-29.8 0 0-11.4-29.1-27.8-36.6 0 0-22.9-15.7 1.6-15.4 0 0 24.9 2 38.6 25.8 21.9 38.6 58.6 27.5 72.9 20.9 2.3-16 8.8-27.1 16-33.7-55.9-6.2-112.3-14.3-112.3-110.5 0-27.5 7.6-41.3 23.6-58.9-2.6-6.5-11.1-33.3 2.6-67.9 20.9-6.5 69 27 69 27 20-5.6 41.5-8.5 62.8-8.5s42.8 2.9 62.8 8.5c0 0 48.1-33.6 69-27 13.7 34.7 5.2 61.4 2.6 67.9 16 17.7 25.8 31.5 25.8 58.9 0 96.5-58.9 104.2-114.8 110.5 9.2 7.9 17 22.9 17 46.4 0 33.7-.3 75.4-.3 83.6 0 6.5 4.6 14.4 17.3 12.1C428.2 457.8 496 362.9 496 252 496 113.3 383.5 8 244.8 8z"/>
  </svg>
);

const AppleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 384 512" fill="currentColor">
    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
  </svg>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem('voyx-lang') || 'fr');
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState('idle');
  const [breathingFrequency, setBreathingFrequency] = useState(0);

  const t = CONTENT[lang] || CONTENT['fr'];

  const toggleLang = () => setLang(prev => { const next = prev === 'fr' ? 'en' : 'fr'; localStorage.setItem('voyx-lang', next); return next; });
  const handleLogin = () => navigate('/login');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setEmailStatus('invalid');
      setTimeout(() => setEmailStatus('idle'), 3000);
      return;
    }
    setEmailStatus('submitting');
    try {
      const existing = await getDocs(query(collection(db, 'waitlist'), where('email', '==', trimmed)));
      if (!existing.empty) {
        setEmailStatus('already');
        setEmail('');
        setTimeout(() => setEmailStatus('idle'), 4000);
        return;
      }
      await addDoc(collection(db, 'waitlist'), {
        email: trimmed,
        createdAt: serverTimestamp()
      });
      // Discord notification
      const webhookUrl = import.meta.env.VITE_DISCORD_WEBHOOK_URL;
      if (webhookUrl) {
        fetch(webhookUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            content: `📬 **Nouvelle inscription beta !**\n\`${trimmed}\`\n${new Date().toLocaleString('fr-FR')}`
          })
        }).catch(() => {}); // fire & forget
      }
      setEmailStatus('success');
      setEmail('');
      setTimeout(() => setEmailStatus('idle'), 4000);
    } catch (err) {
      console.error('Waitlist error:', err);
      setEmailStatus('error');
      setTimeout(() => setEmailStatus('idle'), 3000);
    }
  };

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // ─── Breathing: torus au repos sur la landing (pas de fréquence active) ───

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('.reveal-on-scroll').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page">
      <RotateOverlay />

      {/* ─── Navbar ─── */}
      <nav>
        <div className="nav-inner">
          <div className="nav-logo" onClick={scrollToTop}>
            <img src={logo} alt="VOYX" />
          </div>
          <div className="nav-links" />
          <div className="nav-right">
            <button className="nav-lang" onClick={toggleLang}>{t.nav.lang}</button>
            <button className="nav-cta" onClick={handleLogin}>{t.nav.cta}</button>
          </div>
        </div>
      </nav>

      {/* ─── Hero ─── */}
      <section className="hero">
        <div className="container">
          <div className="hero-centered">
            {/* Mobile: classic h1 */}
            <h1 className="hero-h1-mobile">{t.hero.h1}</h1>
            <h2 className="hero-h2 hero-h2-mobile">{t.hero.h2}</h2>

            {/* Desktop left: title stacked on 3 lines + subtitle */}
            <div className="hero-left">
              <div className="hero-title">
                {t.hero.h1.split(' ').map((word, i) => (
                  <span key={i} className="hero-word">{word}</span>
                ))}
              </div>
              <p className="hero-sub-desktop">{t.hero.h2}</p>
            </div>

            <div className="hero-torus">
              <TorusAnimation
                isPlaying={false}
                frequency={0}
                isRecording={false}
                disablePopup={true}
                cameraFov={75}
                restRadius={0.75}
              />
            </div>

            {/* Desktop right: CTA */}
            <div className="hero-right">
              <button className="hero-cta" onClick={handleLogin}>
                {t.hero.cta}
              </button>
            </div>

            {/* Mobile: CTA below torus */}
            <div className="hero-actions-mobile">
              <button className="hero-cta" onClick={handleLogin}>
                {t.hero.cta}
              </button>
            </div>
          </div>
        </div>
      </section>


      {/* ─── Section : Présence (statement) ─── */}
      <section className="presence-section">
        <div className="container">
          <h2 className="presence-statement reveal-on-scroll">{t.section_presence}</h2>
        </div>
      </section>

      {/* ─── Section : Voxy Reveal (keynote style — disposition 3) ─── */}
      <section className="reveal-section">
        <div className="container">
          <div className="reveal-grid">
            {t.section_reveal.cards.map((card, i) => (
              <div key={i} className="reveal-col reveal-on-scroll" style={{ transitionDelay: `${i * 0.2}s` }}>
                <h3 className="reveal-col__title">{t.section_reveal.lines[i]}</h3>
                <p className="reveal-col__desc">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section : Vidéo démo (remplace VOYX OS modes) ─── */}
      {/* TODO: Ajouter vidéo screen recording du mode Miroir (8-10s, autoplay, muted, loop) */}
      {/* <section className="demo-video-section">
        <div className="container">
          <video autoPlay muted loop playsInline className="demo-video">
            <source src="TODO_DEMO_VIDEO.mp4" type="video/mp4" />
          </video>
        </div>
      </section> */}

      {/* ─── Section : Modes (VOYX OS) ─── */}
      <section id="technology" className="modes-section">
        <div className="container">
          <h2 className="modes-title reveal-on-scroll">{t.section_modes_title}</h2>
          <div className="modes-features">
            {t.section3.features.map((feature, i) => (
              <div key={i} className={`modes-feature reveal-on-scroll${i % 2 === 1 ? ' modes-feature--reverse' : ''}`}>
                <div className="modes-feature__content">
                  <h3 className="modes-feature__title">{feature.title}</h3>
                  <p className="modes-feature__desc">{feature.desc}</p>
                </div>
                {feature.img && (
                  <div className="modes-feature__visual">
                    <img src={feature.img} alt={feature.title} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section : CTA Final + Beta Waitlist ─── */}
      <section className="cta-final">
        <div className="container reveal-on-scroll">
          <div className="beta-signup">
            <h3 className="beta-signup__title">{t.beta.title}</h3>
            <p className="beta-signup__desc">{t.beta.desc}</p>
            <form className="beta-signup__form" onSubmit={handleEmailSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t.beta.placeholder}
                className="beta-signup__input"
                disabled={emailStatus === 'submitting'}
              />
              <button
                type="submit"
                className="hero-cta beta-signup__cta"
                disabled={emailStatus === 'submitting'}
              >
                {emailStatus === 'submitting' ? t.beta.submitting : t.beta.cta}
              </button>
            </form>
            {emailStatus === 'success' && (
              <p className="beta-signup__status beta-signup__status--success">{t.beta.success}</p>
            )}
            {emailStatus === 'already' && (
              <p className="beta-signup__status beta-signup__status--success">{t.beta.already}</p>
            )}
            {emailStatus === 'error' && (
              <p className="beta-signup__status beta-signup__status--error">{t.beta.error}</p>
            )}
            {emailStatus === 'invalid' && (
              <p className="beta-signup__status beta-signup__status--error">{t.beta.invalid}</p>
            )}
          </div>
          <div className="cta-final__opensource">
            <span>{t.section_cta.opensource}</span>
            <a
              href="https://github.com/Rakoues"
              target="_blank"
              rel="noopener noreferrer"
              className="github-btn"
            >
              <GitHubIcon />
              {t.section_cta.github}
            </a>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer>
        <div className="container footer-inner">
          <div className="footer-brand">
            <img src={logo} alt="VOYX" onClick={scrollToTop} />
            <span>&copy; 2026 VOYX. All rights reserved.</span>
          </div>
          <div className="footer-links">
            {t.footer.links.map((link, i) => (
              <a key={i} onClick={() => navigate(link.path)}>{link.label}</a>
            ))}
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
